import { Button } from "@anima/ui/components/button";
import { Input } from "@anima/ui/components/input";
import { Label } from "@anima/ui/components/label";
import { useForm } from "@tanstack/react-form";
import Link from "next/link";
import { useRouter } from "@anima/ui/hooks/use-router";
import { useState } from "react";
import { toast } from "sonner";
import z from "zod";
import { resendEmailVerificationOTP, verifyEmailOTP } from "@/lib/auth-email-otp";
import { authClient } from "@/lib/auth-client";
import Loader from "./loader";
function isEmailVerificationError(error: {
  code?: string;
  message?: string;
  statusText?: string;
}) {
  const message = `${error.message ?? ""} ${error.statusText ?? ""}`.toLowerCase();
  return error.code === "EMAIL_NOT_VERIFIED" || message.includes("email not verified");
}
export default function SignInForm() {
  const router = useRouter();
  const {
    isPending
  } = authClient.useSession();
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const form = useForm({
    defaultValues: {
      email: "",
      password: ""
    },
    onSubmit: async ({
      value
    }) => {
      await authClient.signIn.email({
        email: value.email,
        password: value.password
      }, {
        onSuccess: () => {
          router.push("/");
          toast.success("Sign in successful");
        },
        onError: error => {
          if (isEmailVerificationError(error.error)) {
            setVerificationEmail(value.email);
            toast.success("Verification code sent");
            return;
          }
          toast.error(error.error.message || error.error.statusText);
        }
      });
    },
    validators: {
      onSubmit: z.object({
        email: z.email("Invalid email address"),
        password: z.string().min(8, "Password must be at least 8 characters")
      })
    }
  });
  if (isPending) {
    return <Loader />;
  }
  if (verificationEmail) {
    return <div className="mx-auto w-full mt-10 max-w-md p-6">
        <h1 className="mb-6 text-center text-3xl font-bold">Verify Email</h1>

        <form onSubmit={async event => {
        event.preventDefault();
        setIsVerifying(true);
        try {
          await verifyEmailOTP(verificationEmail, otp);
          toast.success("Email verified");
          router.push("/");
        } catch (error_0) {
          toast.error(error_0 instanceof Error ? error_0.message : "Verification failed");
        } finally {
          setIsVerifying(false);
        }
      }} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="verification-code">Code sent to {verificationEmail}</Label>
            <Input id="verification-code" value={otp} inputMode="numeric" autoComplete="one-time-code" maxLength={6} onChange={event_0 => setOtp(event_0.target.value.replace(/\D/g, ""))} />
          </div>

          <Button type="submit" className="w-full" disabled={otp.length < 6 || isVerifying}>
            {isVerifying ? "Verifying..." : "Verify Email"}
          </Button>
        </form>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-center">
          <Button variant="link" disabled={isResending} onClick={async () => {
          setIsResending(true);
          try {
            await resendEmailVerificationOTP(verificationEmail);
            toast.success("Verification code resent");
          } catch (error_1) {
            toast.error(error_1 instanceof Error ? error_1.message : "Could not resend code");
          } finally {
            setIsResending(false);
          }
        }} className="text-indigo-600 hover:text-indigo-800">
            {isResending ? "Sending..." : "Resend code"}
          </Button>

          <Button variant="link" onClick={() => {
          setVerificationEmail(null);
          setOtp("");
        }} className="text-indigo-600 hover:text-indigo-800">
            Back to sign in
          </Button>
        </div>
      </div>;
  }
  return <div className="mx-auto w-full mt-10 max-w-md p-6">
      <h1 className="mb-6 text-center text-3xl font-bold">Welcome Back</h1>

      <form onSubmit={e => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    }} className="space-y-4">
        <div>
          <form.Field name="email">
            {field => <div className="space-y-2">
                <Label htmlFor={field.name}>Email</Label>
                <Input id={field.name} name={field.name} type="email" value={field.state.value} onBlur={field.handleBlur} onChange={e_0 => field.handleChange(e_0.target.value)} />
                {field.state.meta.errors.map(error_2 => <p key={error_2?.message} className="text-destructive">
                    {error_2?.message}
                  </p>)}
              </div>}
          </form.Field>
        </div>

        <div>
          <form.Field name="password">
            {field_0 => <div className="space-y-2">
                <Label htmlFor={field_0.name}>Password</Label>
                <Input id={field_0.name} name={field_0.name} type="password" value={field_0.state.value} onBlur={field_0.handleBlur} onChange={e_1 => field_0.handleChange(e_1.target.value)} />
                {field_0.state.meta.errors.map(error_3 => <p key={error_3?.message} className="text-destructive">
                    {error_3?.message}
                  </p>)}
              </div>}
          </form.Field>
        </div>

        <form.Subscribe selector={state => ({
        canSubmit: state.canSubmit,
        isSubmitting: state.isSubmitting
      })}>
          {({
          canSubmit,
          isSubmitting
        }) => <Button type="submit" className="w-full" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? "Submitting..." : "Sign In"}
            </Button>}
        </form.Subscribe>
      </form>

      <div className="mt-4 text-center">
        <Link href="/sign-up" className="text-sm font-medium text-indigo-600 underline-offset-4 hover:text-indigo-800 hover:underline">
          Need an account? Sign Up
        </Link>
      </div>
    </div>;
}